import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Lock, Smartphone } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { subscribeToPush, isPushSupported, isIOSSafari, isStandalone } from '../lib/push';

export default function NotificationPreferencesPage() {
  const navigate = useNavigate();
  // Email prefs (existing)
  const [prefs, setPrefs]     = useState({});
  const [types, setTypes]     = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  // Push prefs (new)
  const [pushPrefs, setPushPrefs]   = useState({});
  const [pushTypes, setPushTypes]   = useState({});
  const [pushPermission, setPushPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [savingPush, setSavingPush] = useState(false);

  useEffect(() => {
    Promise.all([
      api.getNotificationPrefs().catch(() => ({ preferences: {}, types: {} })),
      api.getPushPrefs().catch(() => ({ preferences: {}, types: {} })),
    ]).then(([emailData, pushData]) => {
      setPrefs(emailData.preferences || {});
      setTypes(emailData.types || {});
      setPushPrefs(pushData.preferences || {});
      setPushTypes(pushData.types || {});
    }).finally(() => setLoading(false));
  }, []);

  const toggle = (key) => setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  const togglePush = (key) => setPushPrefs(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateNotificationPrefs(prefs);
      toast.success('Email preferences saved!');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleSavePush = async () => {
    setSavingPush(true);
    try {
      await api.updatePushPrefs(pushPrefs);
      toast.success('Push preferences saved!');
    } catch (e) { toast.error(e.message); }
    setSavingPush(false);
  };

  const enablePush = async () => {
    const res = await subscribeToPush({ silent: false });
    setPushPermission(Notification.permission);
    if (res.ok) toast.success('Push notifications enabled!');
    else if (res.reason === 'ios_not_installed') toast.info('On iOS, add to Home Screen first from Safari');
    else if (res.reason === 'denied') toast.error('Permission denied in browser settings');
    else toast.error('Could not enable push: ' + (res.reason || 'unknown'));
  };

  const sendTest = async () => {
    try { await api.pushTest(); toast.success('Test push sent!'); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  const MANDATORY_META = [
    { key: 'password_reset',      label: 'Password Reset Code',       desc: 'The 6-digit code to reset your password' },
    { key: 'welcome',             label: 'Welcome Email',              desc: 'Sent once when your account is created' },
    { key: 'funded_account_ready',label: 'Funded Account Ready',       desc: 'When your funded account credentials are uploaded' },
    { key: 'payout_approved',     label: 'Payout Approved',            desc: 'When your withdrawal has been processed' },
  ];

  const needsIOSInstall = isIOSSafari() && !isStandalone();
  const pushSupported = isPushSupported();

  return (
    <div className="page-container">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)', position: 'sticky', top: 0, background: 'var(--pp-bg)', zIndex: 20 }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>NOTIFICATIONS</h1>
      </div>

      <div style={{ padding: '16px' }}>
        {/* ==================== PUSH NOTIFICATIONS ==================== */}
        <p className="section-label" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Smartphone size={13} /> Push Notifications (Locked-Screen Alerts)
        </p>

        {!pushSupported && (
          <div data-testid="push-unsupported" style={{ background: 'rgba(255,176,0,0.1)', border: '1px solid rgba(255,176,0,0.3)', padding: 14, marginBottom: 16, fontSize: 12, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
            Your browser doesn't support web push. Use Chrome, Safari 16.4+, or Firefox for trade alerts.
          </div>
        )}
        {pushSupported && needsIOSInstall && (
          <div data-testid="push-ios-install" style={{ background: 'rgba(41,121,255,0.1)', border: '1px solid rgba(41,121,255,0.3)', padding: 14, marginBottom: 16, fontSize: 12, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
            iOS requires installing ProfitPulsePro to your Home Screen first. Tap Share → Add to Home Screen in Safari.
          </div>
        )}
        {pushSupported && !needsIOSInstall && pushPermission !== 'granted' && (
          <button data-testid="enable-push-btn" onClick={enablePush} className="btn-primary" style={{ width: '100%', height: 46, marginBottom: 16 }}>
            ENABLE PUSH NOTIFICATIONS
          </button>
        )}
        {pushPermission === 'granted' && (
          <button data-testid="send-test-push-btn" onClick={sendTest} style={{ width: '100%', height: 40, background: 'transparent', border: '1px solid var(--pp-success)', color: 'var(--pp-success)', cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', marginBottom: 16 }}>
            SEND TEST PUSH
          </button>
        )}

        <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', marginBottom: 10 }}>
          {Object.entries(pushTypes).map(([key, meta], i) => {
            const enabled = pushPrefs[key] !== false;
            return (
              <div key={key} style={{ padding: '14px 16px', borderBottom: i < Object.keys(pushTypes).length - 1 ? '1px solid var(--pp-border)' : 'none', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 34, height: 34, background: enabled ? 'rgba(0,255,148,0.12)' : 'var(--pp-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Smartphone size={16} color={enabled ? 'var(--pp-success)' : 'var(--pp-text-3)'} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: enabled ? 'var(--pp-text)' : 'var(--pp-text-3)' }}>{meta.label}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{meta.desc}</p>
                </div>
                <button data-testid={`toggle-push-${key}`} onClick={() => togglePush(key)} style={{ width: 40, height: 22, background: enabled ? 'rgba(0,255,148,0.3)' : 'var(--pp-border)', border: `1px solid ${enabled ? 'var(--pp-success)' : 'var(--pp-border-active)'}`, borderRadius: 11, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', padding: '0 3px', flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: enabled ? 'var(--pp-success)' : 'var(--pp-text-3)', transition: 'transform 150ms, background 150ms', transform: `translateX(${enabled ? 18 : 0}px)` }} />
                </button>
              </div>
            );
          })}
        </div>
        <button data-testid="save-push-prefs-btn" onClick={handleSavePush} disabled={savingPush} style={{ width: '100%', height: 46, background: 'var(--pp-success)', border: 'none', color: '#000', fontSize: 12, fontWeight: 800, letterSpacing: '0.15em', cursor: 'pointer', marginBottom: 28 }}>
          {savingPush ? 'SAVING...' : 'SAVE PUSH PREFERENCES'}
        </button>

        {/* ==================== EMAIL NOTIFICATIONS ==================== */}
        <p className="section-label" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bell size={13} /> Email Notifications
        </p>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
          All emails are sent from <strong style={{ color: 'var(--pp-accent)' }}>support@profit-pulse.pro</strong>. Mandatory operational emails cannot be disabled.
        </p>

        <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', marginBottom: 14 }}>
          {Object.entries(types).map(([key, meta], i) => {
            const enabled = prefs[key] !== false;
            return (
              <div key={key} style={{ padding: '14px 16px', borderBottom: i < Object.keys(types).length - 1 ? '1px solid var(--pp-border)' : 'none', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 34, height: 34, background: enabled ? 'rgba(41,121,255,0.12)' : 'var(--pp-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Bell size={16} color={enabled ? 'var(--pp-accent)' : 'var(--pp-text-3)'} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: enabled ? 'var(--pp-text)' : 'var(--pp-text-3)' }}>{meta.label}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{meta.desc}</p>
                </div>
                <button data-testid={`toggle-email-${key}`} onClick={() => toggle(key)} style={{ width: 40, height: 22, background: enabled ? 'rgba(41,121,255,0.3)' : 'var(--pp-border)', border: `1px solid ${enabled ? 'var(--pp-accent)' : 'var(--pp-border-active)'}`, borderRadius: 11, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', padding: '0 3px', flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: enabled ? 'var(--pp-accent)' : 'var(--pp-text-3)', transition: 'transform 150ms, background 150ms', transform: `translateX(${enabled ? 18 : 0}px)` }} />
                </button>
              </div>
            );
          })}
        </div>

        <button data-testid="save-email-prefs-btn" onClick={handleSave} disabled={saving} className="btn-primary" style={{ width: '100%', height: 46, marginBottom: 24 }}>
          {saving ? 'SAVING...' : 'SAVE EMAIL PREFERENCES'}
        </button>

        {/* Mandatory */}
        <p className="section-label" style={{ marginBottom: 10 }}>Mandatory Emails (Always On)</p>
        <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
          {MANDATORY_META.map((item, i) => (
            <div key={item.key} style={{ padding: '12px 16px', borderBottom: i < MANDATORY_META.length - 1 ? '1px solid var(--pp-border)' : 'none', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 34, height: 34, background: 'var(--pp-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Lock size={14} color="var(--pp-text-3)" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--pp-text-3)' }}>{item.label}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{item.desc}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00FF94' }} />
                <span style={{ fontSize: 9, color: '#00FF94', fontWeight: 700, letterSpacing: '0.1em' }}>ALWAYS ON</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
